/**
 * Formate un nombre avec des séparateurs de milliers
 * @param value - La valeur à formater
 * @returns La valeur formatée avec des séparateurs de milliers
 */
export const formatNumber = (value: number | string): string => {
  // Si la valeur est déjà une chaîne, on essaie de la convertir en nombre
  if (typeof value === 'string') {
    // On essaie de convertir en nombre, si ce n'est pas possible, on retourne la chaîne telle quelle
    const num = Number(value);
    return isNaN(num) ? value : num.toLocaleString('fr-FR');
  }
  
  // Si c'est un nombre, on le formate simplement
  return value.toLocaleString('fr-FR');
};

/**
 * Formate une date au format français
 * @param date - La date à formater (chaîne, timestamp ou objet Date)
 * @returns La date formatée au format jj/mm/aaaa
 */
export const formatDate = (date: string | number | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR');
};

/**
 * Tronque un texte à une certaine longueur et ajoute des points de suspension si nécessaire
 * @param text - Le texte à tronquer
 * @param maxLength - La longueur maximale du texte
 * @returns Le texte tronqué avec des points de suspension si nécessaire
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Formate un montant monétaire
 * @param amount - Le montant à formater
 * @param currency - La devise (par défaut: 'XOF' pour Franc CFA)
 * @returns Le montant formaté avec le symbole de la devise
 */
export const formatCurrency = (amount: number | null | undefined, currency: string = 'XOF'): string => {
  if (amount === null || amount === undefined || isNaN(Number(amount))) {
    return '-';
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(amount));
};

/**
 * Formate un montant en Francs CFA
 * @param amount - Le montant à formater
 * @returns Le montant formaté en F CFA
 */
export const formatCFA = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '0 F CFA'; // Ou une autre valeur par défaut
  }
  return `${amount.toLocaleString('fr-FR')} F CFA`;
};

/**
 * Formate une durée en minutes en heures et minutes
 * @param minutes - La durée en minutes
 * @returns La durée formatée (ex: "2h 30min")
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
};

/**
 * Formate une référence de devis si elle est en format hérité DEV-AAMMJJ-HHMM-ID vers DEV-JJMMAA-HHMM-ID
 */
export const formatQuoteReference = (ref: string | null | undefined): string => {
  if (!ref) return '';
  const match = ref.match(/^DEV-(\d{2})(\d{2})(\d{2})-(.*)$/);
  if (match) {
    const [, yy, mm, dd, rest] = match;
    return `DEV-${dd}${mm}${yy}-${rest}`;
  }
  return ref;
};
